/**
 * Tab: Regras de alerta — inventário e edição avançada.
 *
 * Esta tela e a aba Thresholds do ativo gravam na MESMA tabela
 * (`alert_rules`). Elas não são dois caminhos concorrentes de criação —
 * cada uma tem um papel:
 *
 *   • Ativo → Thresholds : editor tipado, um campo por parâmetro, com o
 *     valor herdado visível. É o caminho normal.
 *   • Aqui                 : visão de TODAS as regras do cliente de uma vez
 *     (quem sobrescreve o quê), e o JSON cru pros kinds que ainda não têm
 *     formulário tipado — power_overload, current_spike.
 *
 * Quando as duas divergirem em vocabulário, o ativo manda: é a tela que o
 * cliente usa.
 */

import { useEffect, useMemo, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { ArrowUpRight, CornerDownRight, Pencil, Plus, Trash2 } from "lucide-react";
import toast from "react-hot-toast";
import { Link } from "react-router-dom";

import { alertRulesApi } from "@/api/alerts";
import { apiErrorMessage } from "@/api/client";
import {
  alertQk,
  useAlertRules,
  useEquipments,
} from "@/hooks/queries";
import { KINDS_CRIAVEIS, opcoesDeKind } from "@/lib/ruleKinds";
import type {
  AlertRule,
  AlertRuleCreate,
  RuleKind,
  Severity,
} from "@/types";

import { Button } from "@/components/Button";
import { DataTable, type Column } from "@/components/DataTable";
import { Modal } from "@/components/Modal";
import { Field, Select } from "@/components/Field";
import { Tip } from "@/components/Tip";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { Badge } from "@/components/Badge";
import { SEVERIDADE_OPCOES, SEVERIDADE_TOM } from "@/lib/severidade";

// Os cross-device (site_power_outage, tenant_silent) ficam de fora: o
// engine os pós-processa por site e por tenant, então criá-los aqui como
// regra por equipamento não faria efeito nenhum.
const KIND_OPTIONS = opcoesDeKind(KINDS_CRIAVEIS);

const SEVERITY_OPTIONS = SEVERIDADE_OPCOES;

const SEVERITY_TONE = SEVERIDADE_TOM;

type ScopeFilter = "tenant" | "overrides" | "all";

export function AlertRulesTab({ tenantId }: { tenantId?: string }) {
  const rules = useAlertRules(tenantId);
  const equipments = useEquipments();
  const qc = useQueryClient();

  const [isCreating, setIsCreating] = useState(false);
  const [editTarget, setEditTarget] = useState<AlertRule | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<AlertRule | null>(null);
  const [scopeFilter, setScopeFilter] = useState<ScopeFilter>("tenant");

  // Conta quantos overrides existem por kind (pra mostrar "+N overrides"
  // no badge da linha tenant-wide).
  const overrideCountByKind = useMemo(() => {
    const map = new Map<RuleKind, number>();
    for (const r of rules.data ?? []) {
      if (r.equipment_id) {
        map.set(r.kind, (map.get(r.kind) ?? 0) + 1);
      }
    }
    return map;
  }, [rules.data]);

  // Lista ordenada e filtrada. Ordem default: agrupa por kind, com a tenant-wide
  // vindo PRIMEIRO seguida pelos seus overrides (recuados visualmente).
  const visibleRules = useMemo(() => {
    const list = rules.data ?? [];
    const filtered = list.filter((r) => {
      if (scopeFilter === "tenant")    return !r.equipment_id;
      if (scopeFilter === "overrides") return !!r.equipment_id;
      return true;
    });
    if (scopeFilter !== "all") return filtered;
    // "all": agrupar por kind com tenant-wide primeiro
    const order: AlertRule[] = [];
    const tenantWide = list.filter((r) => !r.equipment_id);
    const overrides  = list.filter((r) => !!r.equipment_id);
    const kinds = new Set([...tenantWide.map((r) => r.kind), ...overrides.map((r) => r.kind)]);
    for (const kind of kinds) {
      order.push(...tenantWide.filter((r) => r.kind === kind));
      order.push(...overrides.filter((r) => r.kind === kind));
    }
    // Inclui rules que sobraram por kind não conhecido (defensivo)
    for (const r of list) if (!order.includes(r)) order.push(r);
    return order;
  }, [rules.data, scopeFilter]);

  const equipmentNameById = new Map(
    (equipments.data ?? []).map((e) => [e.id, e.name]),
  );

  const createMut = useMutation({
    mutationFn: (p: AlertRuleCreate) => alertRulesApi.create(p, tenantId),
    onSuccess: () => {
      toast.success("Regra criada");
      // Invalida prefix-only ['alert-rules'] pra cobrir variações de tenantId.
      void qc.invalidateQueries({ queryKey: ["alert-rules"] });
      setIsCreating(false);
    },
    onError: (e) => toast.error(apiErrorMessage(e)),
  });

  const updateMut = useMutation({
    mutationFn: ({ id, p }: { id: string; p: AlertRuleCreate }) =>
      alertRulesApi.update(id, {
        equipment_id: p.equipment_id,
        severity: p.severity,
        params: p.params,
        enabled: p.enabled,
      }),
    onSuccess: () => {
      toast.success("Regra atualizada");
      // Invalida prefix-only ['alert-rules'] pra cobrir variações de tenantId.
      void qc.invalidateQueries({ queryKey: ["alert-rules"] });
      setEditTarget(null);
    },
    onError: (e) => toast.error(apiErrorMessage(e)),
  });

  const deleteMut = useMutation({
    mutationFn: (id: string) => alertRulesApi.remove(id),
    onSuccess: () => {
      toast.success("Regra deletada");
      // Invalida prefix-only ['alert-rules'] pra cobrir variações de tenantId.
      void qc.invalidateQueries({ queryKey: ["alert-rules"] });
      setDeleteTarget(null);
    },
    onError: (e) => toast.error(apiErrorMessage(e)),
  });

  const columns: Column<AlertRule>[] = [
    {
      key: "kind",
      header: "Tipo",
      cell: (r) => {
        const label = KIND_OPTIONS.find((k) => k.value === r.kind)?.label ?? r.kind;
        if (r.equipment_id && scopeFilter === "all") {
          // Linha override no modo "all" — recuar pra deixar claro a hierarquia
          return (
            <span className="inline-flex items-center gap-1 pl-5 text-slate-600 dark:text-slate-400">
              <CornerDownRight className="h-3 w-3 text-slate-400" />
              {label}
            </span>
          );
        }
        return (
          <span className="font-medium text-slate-900 dark:text-slate-100">{label}</span>
        );
      },
    },
    {
      key: "severity",
      header: "Severidade",
      cell: (r) => <Badge tone={SEVERITY_TONE[r.severity]}>{r.severity}</Badge>,
    },
    {
      key: "scope",
      header: "Escopo",
      cell: (r) => {
        if (r.equipment_id) {
          return (
            <span className="text-xs text-slate-600 dark:text-slate-400">
              {equipmentNameById.get(r.equipment_id) ?? r.equipment_id.slice(0, 8)}
            </span>
          );
        }
        // Tenant-wide — indica se há overrides
        const nOver = overrideCountByKind.get(r.kind) ?? 0;
        return (
          <div className="flex items-center gap-2">
            <Badge tone="brand">tenant inteiro</Badge>
            {nOver > 0 && (
              <span className="text-[11px] text-slate-500 dark:text-slate-400">
                + {nOver} override{nOver === 1 ? "" : "s"}
              </span>
            )}
          </div>
        );
      },
    },
    {
      key: "enabled",
      header: "Status",
      cell: (r) =>
        r.enabled ? (
          <Badge tone="success">ativa</Badge>
        ) : (
          <Badge tone="default">desabilitada</Badge>
        ),
      hideOnMobile: true,
    },
    {
      key: "actions",
      header: "",
      align: "right",
      // Override NÃO se edita aqui.
      //
      // As duas telas gravavam na mesma tabela e ofereciam o mesmo ajuste com
      // vocabulários diferentes — aqui JSON cru, lá formulário por campo com o
      // valor herdado à vista. Ter os dois caminhos não era escolha, era
      // duplicação: quem editasse pelo JSON não via o que estava herdando.
      //
      // Agora cada tela tem um escopo só. Esta cuida do que vale para o
      // CLIENTE; o ajuste de um ativo específico vive no ativo, e o botão leva
      // até lá em vez de fingir que os dois lugares são equivalentes.
      cell: (r) =>
        r.equipment_id ? (
          <div className="flex justify-end">
            <Link
              to={`/equipments/${r.equipment_id}?tab=thresholds`}
              className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs font-medium
                         text-brand-600 hover:bg-brand-50 dark:text-brand-400 dark:hover:bg-brand-900/20"
            >
              Ajustar no ativo
              <ArrowUpRight className="h-3.5 w-3.5" />
            </Link>
          </div>
        ) : (
          <div className="flex justify-end gap-1">
            <Button size="sm" variant="ghost" icon={<Pencil className="h-4 w-4" />} onClick={() => setEditTarget(r)}>
              Editar
            </Button>
            <Button size="sm" variant="ghost" icon={<Trash2 className="h-4 w-4 text-red-500" />} onClick={() => setDeleteTarget(r)} />
          </div>
        ),
    },
  ];

  const totalOverrides = (rules.data ?? []).filter((r) => !!r.equipment_id).length;

  return (
    <div className="space-y-4">
      {/* Diz o papel desta tela — ela e a aba Thresholds do ativo gravam na
          mesma tabela, e sem isso pareciam dois caminhos concorrentes. */}
      <p className="text-xs text-slate-500 dark:text-slate-400">
        Aqui se define o que vale para o <strong>cliente inteiro</strong>, e se
        enxerga quais ativos fogem desse padrão. O ajuste de um ativo
        específico é feito no próprio ativo, em{" "}
        <span className="font-medium text-slate-700 dark:text-slate-300">
          Ativo › Limites
        </span>
        , onde o formulário mostra o valor herdado ao lado do que você digita.
      </p>

      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="inline-flex rounded-md border border-surface-200 dark:border-slate-700 overflow-hidden text-xs">
          {([
            { value: "tenant",    label: "Tenant inteiro" },
            { value: "overrides", label: `Overrides (${totalOverrides})` },
            { value: "all",       label: "Todos" },
          ] as { value: ScopeFilter; label: string }[]).map((opt) => {
            const active = scopeFilter === opt.value;
            return (
              <button
                key={opt.value}
                type="button"
                onClick={() => setScopeFilter(opt.value)}
                className={
                  "px-3 py-1.5 font-medium transition-colors " +
                  (active
                    ? "bg-brand-50 text-brand-700 dark:bg-brand-950/50 dark:text-brand-300"
                    : "bg-white dark:bg-slate-900 text-slate-600 dark:text-slate-400 hover:bg-surface-50 dark:hover:bg-slate-800")
                }
              >
                {opt.label}
              </button>
            );
          })}
        </div>
        <Button icon={<Plus className="h-4 w-4" />} onClick={() => setIsCreating(true)}>
          Nova regra
        </Button>
      </div>

      {scopeFilter === "tenant" && totalOverrides > 0 && (
        <div className="text-xs text-slate-500 dark:text-slate-400 px-1">
          {totalOverrides} override{totalOverrides === 1 ? "" : "s"} oculto{totalOverrides === 1 ? "" : "s"} —
          {" "}gerenciados no painel "Alertas e thresholds" de cada ativo.
        </div>
      )}

      <DataTable
        data={visibleRules}
        columns={columns}
        loading={rules.isLoading}
        rowKey={(r) => r.id}
        emptyTitle={
          scopeFilter === "overrides"
            ? "Sem overrides em ativos"
            : "Nenhuma regra configurada"
        }
        emptyDescription={
          scopeFilter === "overrides"
            ? "Overrides são criados no painel 'Alertas e thresholds' de cada ativo."
            : "Crie regras pra começar a monitorar (temp out of range, offline, bateria, etc)."
        }
      />

      <RuleFormModal
        key={isCreating ? "create" : `edit-${editTarget?.id ?? "none"}`}
        open={isCreating || !!editTarget}
        onClose={() => {
          setIsCreating(false);
          setEditTarget(null);
        }}
        title={isCreating ? "Nova regra" : "Editar regra"}
        initial={editTarget}
        equipmentOptions={[
          { value: "", label: "(tenant inteiro)" },
          ...(equipments.data ?? []).map((e) => ({ value: e.id, label: e.name })),
        ]}
        submitting={createMut.isPending || updateMut.isPending}
        onSubmit={(p) => {
          if (editTarget) updateMut.mutate({ id: editTarget.id, p });
          else            createMut.mutate(p);
        }}
      />

      <ConfirmDialog
        open={!!deleteTarget}
        title="Deletar regra?"
        message={
          deleteTarget
            ? `A regra '${KIND_OPTIONS.find((k) => k.value === deleteTarget.kind)?.label}' será removida.`
            : ""
        }
        confirmLabel="Deletar"
        loading={deleteMut.isPending}
        onCancel={() => setDeleteTarget(null)}
        onConfirm={() => deleteTarget && deleteMut.mutate(deleteTarget.id)}
      />
    </div>
  );
}

// ---------- form modal ------------------------------------------------------

function RuleFormModal({
  open, onClose, title, initial, equipmentOptions, submitting, onSubmit,
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  initial?: AlertRule | null;
  equipmentOptions: { value: string; label: string }[];
  submitting?: boolean;
  onSubmit: (p: AlertRuleCreate) => void;
}) {
  const [kind, setKind] = useState<RuleKind>(initial?.kind ?? "temp_out_of_range");
  const [severity, setSeverity] = useState<Severity>(initial?.severity ?? "warning");
  const [equipmentId, setEquipmentId] = useState<string>(initial?.equipment_id ?? "");
  const [enabled, setEnabled] = useState<boolean>(initial?.enabled ?? true);
  const [paramsJson, setParamsJson] = useState<string>(
    initial ? JSON.stringify(initial.params ?? {}, null, 2) : "{}",
  );

  useEffect(() => {
    setKind(initial?.kind ?? "temp_out_of_range");
    setSeverity(initial?.severity ?? "warning");
    setEquipmentId(initial?.equipment_id ?? "");
    setEnabled(initial?.enabled ?? true);
    setParamsJson(initial ? JSON.stringify(initial.params ?? {}, null, 2) : "{}");
  }, [initial]);

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    let params: Record<string, unknown> = {};
    try {
      params = paramsJson.trim() ? JSON.parse(paramsJson) : {};
    } catch {
      toast.error("Parâmetros não são JSON válido");
      return;
    }
    onSubmit({
      kind,
      severity,
      equipment_id: equipmentId || null,
      enabled,
      params,
    });
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={title}
      size="lg"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={submitting}>
            Cancelar
          </Button>
          <Button type="submit" form="rule-form" loading={submitting}>
            Salvar
          </Button>
        </>
      }
    >
      <form id="rule-form" onSubmit={handleSubmit} className="space-y-4">
        <Field label="Tipo da regra" required>
          <Select
            value={kind}
            onChange={(e) => setKind(e.target.value as RuleKind)}
            options={KIND_OPTIONS}
            disabled={!!initial}
          />
        </Field>

        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Severidade" required>
            <Select
              value={severity}
              onChange={(e) => setSeverity(e.target.value as Severity)}
              options={SEVERITY_OPTIONS}
            />
          </Field>
          {/* O seletor de ativo saiu daqui.
              Criar override por esta tela era o segundo caminho para a mesma
              coisa, com vocabulário diferente e sem mostrar o valor herdado —
              e foi assim que um setpoint do cliente passou a brigar com o do
              ativo sem ninguém ver. Override se cria no ativo.

              Em EDIÇÃO o campo continua existindo (uma regra que já é override
              precisa saber a quem pertence), mas a tabela manda essa linha
              para o ativo antes de chegar aqui. */}
          <Field label="Aplica-se a">
            <div className="flex h-9 items-center rounded-lg border border-surface-200 px-3 text-sm text-slate-600 dark:border-slate-700 dark:text-slate-400">
              {equipmentId
                ? equipmentOptions.find((o) => o.value === equipmentId)?.label ?? "um ativo"
                : "Todos os ativos deste cliente"}
            </div>
          </Field>
        </div>

        <Field
          label={
            <>
              Parâmetros (JSON)
              <Tip label="O que colocar nos parâmetros">
                {paramsHelpFor(kind).porque}
              </Tip>
            </>
          }
        >
          <textarea
            className="input min-h-[120px] font-mono text-xs"
            value={paramsJson}
            onChange={(e) => setParamsJson(e.target.value)}
            placeholder={paramsHelpFor(kind).exemplo}
          />
        </Field>

        <label className="flex items-center gap-2 text-sm text-slate-700 dark:text-slate-300">
          <input
            type="checkbox"
            checked={enabled}
            onChange={(e) => setEnabled(e.target.checked)}
            className="h-4 w-4 rounded border-surface-300 dark:border-slate-700 text-brand-600"
          />
          Regra ativa
        </label>
      </form>
    </Modal>
  );
}

/**
 * Ajuda dos parâmetros, separada em duas partes com destinos diferentes:
 *
 *   • `exemplo` vira o placeholder do próprio textarea — é lido ENQUANTO se
 *     digita, então esconder atrás de hover obrigaria a largar o teclado.
 *   • `porque` vai pro ícone — é conhecimento de domínio, lido uma vez.
 */
function paramsHelpFor(kind: RuleKind): { exemplo: string; porque: string } {
  switch (kind) {
    case "temp_out_of_range":
      return {
        exemplo: '{"setpoint_min":2,"setpoint_max":8,"role":"temp_internal"}',
        porque:
          "Sobrescreve o range definido no ativo. O role separa termômetro "
          + "interno, de produto e externo — sem ele a regra vale pra todos.",
      };
    case "device_offline":
      return {
        exemplo:
          '{"offline_after_minutes":15,"battery_offline_after_minutes":120,'
          + '"offline_after_minutes_by_role":{"door":720}}',
        porque:
          "Precedência: role > alimentação > genérico. Sensor a bateria dorme "
          + "e o de porta só fala quando abre ou fecha — por isso cada um tem "
          + "seu próprio limite.",
      };
    case "low_battery":
      return {
        exemplo: '{"battery_warning_pct":20,"battery_critical_pct":10}',
        porque: "Sobrescreve os percentuais herdados do cliente.",
      };
    case "weak_signal":
      return {
        exemplo: '{"signal_warning_dbm":-80,"signal_warning_lqi":50}',
        porque:
          "dBm é escala negativa (mais negativo = pior); LQI vai de 0 a 255 "
          + "e maior é melhor.",
      };
    case "door_open_too_long":
      return {
        exemplo: '{"door_open_warning_minutes":5}',
        porque: "Tempo contínuo de porta aberta. Fechar reinicia a contagem.",
      };
    case "power_off":
      return {
        exemplo: '{"threshold_watts":5,"off_minutes":65}',
        porque:
          "Mede potência, não o relé. Como o compressor descansa por longos "
          + "períodos normais, o tempo precisa ficar ACIMA do pior descanso do "
          + "equipamento — senão o alerta dispara no ciclo normal e vira ruído.",
      };
    case "plug_off":
      return {
        exemplo: '{"stale_after_minutes":10}',
        porque:
          "Lê o relé da tomada (state=OFF) — desarme da proteção, que o "
          + "power_off não distingue de descanso. Dispara na primeira leitura. "
          + "O parâmetro não é espera: é a idade máxima da leitura aceita.",
      };
    case "medicao_incoerente":
      return {
        exemplo: '{"window_minutes":15,"max_transicoes_tensao":1}',
        porque:
          "Detecta medidor que continua reportando e parou de medir. A "
          + "testemunha é a tensão: ela oscila sempre, então série de tensão "
          + "parada com o device publicando é medidor repetindo o último "
          + "valor. Enquanto dispara, o compressor desligado fica suspenso — "
          + "o valor congelado engana nos dois sentidos.",
      };
    case "power_overload":
      return {
        exemplo: '{"overload_pct":130,"sustained_minutes":10}',
        porque: "Exige nominal_power_w cadastrado no ativo, senão fica inerte.",
      };
    case "current_spike":
      return {
        exemplo: '{"spike_pct":200}',
        porque:
          "Exige nominal_voltage_v e nominal_power_w no ativo, senão fica "
          + "inerte.",
      };
    default:
      return {
        exemplo: "{}",
        porque: "Em branco, a regra usa os padrões do cliente.",
      };
  }
}
